import { useCallback } from "react"

import { MEZO_ASSETS } from "#/shared/constants"
import { SwapRoute } from "#/shared/types"
import {
  convertFixedPoint,
  getMezoTokenAddress,
  getMezoTokenNameByAddress,
  isSameAddress,
} from "#/shared/utils"

import useSwapAmountsOut from "./useSwapAmountsOut"

const MAX_ITERATIONS = 10
const BTC_PRICE_USD = 100_000n // Approximate BTC price in USD for upper bound estimation

const useSwapAmountsIn = () => {
  const { getAmountsOut } = useSwapAmountsOut()
  const btcAddress = getMezoTokenAddress("mBTC")

  const getAmountsIn = useCallback(
    async (amountOut: bigint, routes: SwapRoute[]) => {
      if (amountOut === 0n) return [0n, 0n]

      const fromTokenAddress = routes[0].from
      const toTokenAddress = routes.at(-1)?.to

      const fromTokenName = getMezoTokenNameByAddress(fromTokenAddress)
      const toTokenName = getMezoTokenNameByAddress(toTokenAddress ?? "0x0")

      if (
        !(
          fromTokenAddress &&
          toTokenAddress &&
          fromTokenName &&
          toTokenName &&
          btcAddress
        )
      ) {
        return [0n, 0n]
      }

      // Adjust value of amountOut to match the amountIn decimal places
      const adjustedAmountOut = convertFixedPoint(
        amountOut,
        Number(MEZO_ASSETS[toTokenName]?.decimals ?? 0),
        Number(MEZO_ASSETS[fromTokenName]?.decimals ?? 0),
      )

      // Based on the expected amount out we can set the expected high bound for
      // for the binary search

      // For stablecoin pairs the high bound is set to 3x the expected amount out
      let high = adjustedAmountOut * 3n

      // Swapping between BTC and stablecoin - setting higher bound based on the
      // average BTC price in USD

      if (isSameAddress(fromTokenAddress, btcAddress)) {
        high = (adjustedAmountOut / BTC_PRICE_USD) * 3n
      }

      if (isSameAddress(toTokenAddress, btcAddress)) {
        high = adjustedAmountOut * BTC_PRICE_USD * 3n
      }

      let low = 1n
      let bestAmountIn = 0n
      let bestAmountOut = 0n

      for (let i = 0; i < MAX_ITERATIONS && low <= high; i += 1) {
        const mid = (low + high) / 2n
        try {
          // eslint-disable-next-line no-await-in-loop
          const result = await getAmountsOut(mid, routes)

          const out = result[1]

          // Early break if the difference is within 1% of the target amount
          const diff = out > amountOut ? out - amountOut : amountOut - out
          if (amountOut !== 0n && (diff * 1000n) / amountOut <= 1n) {
            bestAmountIn = mid
            bestAmountOut = out

            break
          }

          // Choose the new bounds based on the output amount
          if (out >= amountOut) {
            bestAmountIn = mid
            bestAmountOut = out

            high = mid - 1n
          } else {
            low = mid + 1n
          }
        } catch (e) {
          // If contract call fails, increase input
          low = mid + 1n
        }
      }

      return [bestAmountIn, bestAmountOut]
    },
    [btcAddress, getAmountsOut],
  )

  return { getAmountsIn }
}

export default useSwapAmountsIn
